import { useState } from "react";

import Modal from "@/components/ui/Modal.jsx";
import Button from "@/components/ui/Button.jsx";
import { Input, Label, Select, Switch } from "@/components/ui/Form.jsx";
import Tag from "@/components/ui/Tag.jsx";
import { Callout } from "@/components/ui/Display.jsx";
import { limitLabel, priceLabel } from "@/lib/superAdmin.js";

/*
 * Edit one organization: its plan, its platform entitlements, and its contact
 * details.
 *
 * THE ENTITLEMENT IS MARKED DIFFERENTLY FROM EVERYTHING ELSE, deliberately.
 * `clipboardCaptureAllowed` is the only field on this form that an organization
 * admin cannot also change — it is granted here and nowhere else, and it is the
 * first of three gates before clipboard capture actually happens. Styling it
 * like the neighbouring inputs would hide that difference; a superAdmin
 * granting it should see that they are the only one who can.
 */
const ENTITLEMENTS = [
    {
        key: "clipboardCaptureAllowed",
        label: "Allow clipboard capture",
        hint: "Without this the organization's own clipboard switch stays disabled — the first of three gates.",
    },
];

export default function OrgEditModal({ organization, packages, saving, error, onSubmit, onClose }) {
    const [values, setValues] = useState(() => ({
        packageId: organization.packageId ?? "",
        clipboardCaptureAllowed: organization.clipboardAllowed,
        phone: organization.phone,
        country: organization.country,
        website: organization.website,
        address: organization.address,
    }));

    const set = (key, value) => setValues((v) => ({ ...v, [key]: value }));

    /*
     * Only what changed. The endpoint applies each field it is given, and
     * sending the whole record would re-write values another superAdmin may
     * have changed while this modal sat open.
     */
    const patch = () => {
        const body = {};
        if (values.packageId && values.packageId !== organization.packageId)
            body.package = values.packageId;
        if (values.clipboardCaptureAllowed !== organization.clipboardAllowed)
            body.clipboardCaptureAllowed = values.clipboardCaptureAllowed;
        for (const key of ["phone", "country", "website", "address"]) {
            if (values[key] !== organization[key]) body[key] = values[key];
        }
        return body;
    };

    const changes = Object.keys(patch()).length;

    return (
        <Modal
            open
            onClose={onClose}
            size="xl"
            title={organization.name}
            subtitle={organization.identifier}
            footer={
                <>
                    <Button onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        className="ml-auto"
                        disabled={changes === 0}
                        loading={saving}
                        loadingLabel="Saving…"
                        onClick={() => onSubmit(patch())}
                        data-save-org
                    >
                        {changes ? `Save ${changes} change${changes === 1 ? "" : "s"}` : "Save changes"}
                    </Button>
                </>
            }
        >
            <div className="space-y-5">
                {error && <Callout tone="danger" data-org-edit-error>{error}</Callout>}

                <div>
                    <Label htmlFor="ePackage">Package</Label>
                    <Select
                        id="ePackage"
                        size="lg"
                        data-field="package"
                        value={values.packageId}
                        onChange={(e) => set("packageId", e.target.value)}
                    >
                        {packages.map((p) => (
                            <option key={p._id} value={p._id}>
                                {p.name} — {limitLabel(p.features?.maxUsers)} seats,{" "}
                                {p.features?.dataRetention ?? "—"}d retention · {priceLabel(p)}
                            </option>
                        ))}
                    </Select>
                    {organization.seatCap != null && organization.totalUsers > 0 && (
                        <p className="hint" data-seat-note>
                            {organization.totalUsers} of {organization.seatCap} seats used
                            {organization.overSeat && " — already over the current cap"}.
                        </p>
                    )}
                </div>

                <div className="space-y-2.5">
                    {ENTITLEMENTS.map((e) => (
                        <div
                            key={e.key}
                            data-entitlement={e.key}
                            className="flex items-center justify-between gap-4 rounded-lg border border-violet-200 bg-violet-50/50 px-3.5 py-3 dark:border-violet-900 dark:bg-violet-950/20"
                        >
                            <span className="min-w-0">
                                <span className="flex flex-wrap items-center gap-2">
                                    <span className="text-[13px] font-medium">{e.label}</span>
                                    <Tag tone="violet">Entitlement</Tag>
                                </span>
                                <span className="mt-0.5 block text-[11.5px] text-slate-400 dark:text-slate-500">
                                    {e.hint}
                                </span>
                            </span>
                            <Switch
                                tone="violet"
                                checked={values[e.key]}
                                onChange={(ev) => set(e.key, ev.target.checked)}
                                aria-label={e.label}
                            />
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <Label htmlFor="ePhone">Phone</Label>
                        <Input
                            id="ePhone"
                            size="lg"
                            type="tel"
                            placeholder="+880 …"
                            value={values.phone}
                            onChange={(e) => set("phone", e.target.value)}
                        />
                    </div>
                    <div>
                        <Label htmlFor="eCountry">Country</Label>
                        <Input
                            id="eCountry"
                            size="lg"
                            value={values.country}
                            onChange={(e) => set("country", e.target.value)}
                        />
                    </div>
                    <div>
                        <Label htmlFor="eWebsite">Website</Label>
                        <Input
                            id="eWebsite"
                            size="lg"
                            type="url"
                            placeholder="https://…"
                            value={values.website}
                            onChange={(e) => set("website", e.target.value)}
                        />
                    </div>
                    <div>
                        <Label htmlFor="eAddress">Address</Label>
                        <Input
                            id="eAddress"
                            size="lg"
                            value={values.address}
                            onChange={(e) => set("address", e.target.value)}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
}
